import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  Alert,
  IconButton,
  Dialog,
  AppBar,
  Toolbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { AuthContext } from '../../../context/auth-context';
import ProductionMetricsAPI from '../../../../services/api/analytics/production-metrics-api';
import { getProjectDateRangeErrorMessage } from '../../../../utils/projectDateRangeError';
import { toCalendarDate } from '../../../../utils/calendarDate';
import HCChart from '../components/HCChart';
import HCMonthTable from '../components/HCMonthTable';

const DATE_FORMATTER = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  timeZone: 'UTC',
});

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(`${String(value).slice(0, 10)}T00:00:00Z`);
  if (isNaN(date.getTime())) return '—';
  return DATE_FORMATTER.format(date);
}

function formatNumber(value, digits = 2) {
  return (Number(value) || 0).toLocaleString('en-US', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

function ftePoint(week, type) {
  const isShop = type === 'shop';
  const fte = isShop ? week.actualShopFTE : week.actualFieldFTE;
  const workers = isShop ? week.actualShopWorkers : week.actualFieldWorkers;
  const overtimeMultiplier = isShop
    ? week.actualShopOvertimeMultiplier
    : week.actualFieldOvertimeMultiplier;
  return {
    x: toCalendarDate(week.weekEnding),
    y: fte,
    workers,
    overtimeMultiplier,
    avgHours:
      fte != null && workers > 0
        ? Math.round((Number(fte) / workers) * 40)
        : null,
  };
}

const ActivePackagePanel = ({
  drilldown,
  loading,
  error,
  onClose,
  maxTableHeight,
}) => {
  if (!drilldown) return null;

  const rows = drilldown.packages || [];
  const label = drilldown.type === 'shop' ? 'Shop' : 'Field';
  const totalBudgetHours =
    drilldown.totalBudgetHours ??
    rows.reduce((sum, row) => sum + (Number(row.budgetHours) || 0), 0);
  const plannedFTE = drilldown.plannedFTE ?? totalBudgetHours / 40;

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: '0.5px solid',
        borderColor: 'divider',
        borderRadius: 2,
        p: 2,
        mb: 1.5,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 2,
          mb: 1.5,
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: '10px',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              color: 'text.secondary',
            }}
          >
            {label} Active Packages
          </Typography>
          <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>
            Week ending {formatDate(drilldown.weekEnding)}
          </Typography>
          <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
            Budget hours shown are the package hours spread into this chart
            week. Total / 40 = {formatNumber(plannedFTE)} planned FTE.
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} aria-label="Close panel">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={16} />
          <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
            Loading active packages…
          </Typography>
        </Box>
      )}

      {!loading && error && (
        <Alert severity="error" sx={{ fontSize: '12px' }}>
          {error}
        </Alert>
      )}

      {!loading && !error && rows.length === 0 && (
        <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
          No packages contributed planned {label.toLowerCase()} hours during
          this week.
        </Typography>
      )}

      {!loading && !error && rows.length > 0 && (
        <Box
          sx={{
            maxHeight: maxTableHeight,
            overflow: maxTableHeight ? 'auto' : 'visible',
          }}
        >
          <Table
            size="small"
            stickyHeader={Boolean(maxTableHeight)}
            sx={{
              '& .MuiTableCell-root': {
                px: 1,
                py: 0.5,
                fontSize: '12px',
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: 'text.secondary', fontWeight: 600 }}>
                  Package
                </TableCell>
                <TableCell sx={{ color: 'text.secondary', fontWeight: 600 }}>
                  Planned Start
                </TableCell>
                <TableCell sx={{ color: 'text.secondary', fontWeight: 600 }}>
                  Planned Finish
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ color: 'text.secondary', fontWeight: 600 }}
                >
                  Working Days
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ color: 'text.secondary', fontWeight: 600 }}
                >
                  Budget Hrs in Week
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                const code = (row.autodeskName || '').trim();
                const name = (row.packageName || '').trim();
                const packageLabel =
                  code && name && code !== name
                    ? `${code} — ${name}`
                    : code || name || 'Unnamed package';
                return (
                  <TableRow key={row.packageId}>
                    <TableCell>{packageLabel}</TableCell>
                    <TableCell>{formatDate(row.plannedStartDate)}</TableCell>
                    <TableCell>{formatDate(row.plannedFinishDate)}</TableCell>
                    <TableCell align="right">
                      {row.durationWorkingDays}
                    </TableCell>
                    <TableCell align="right">
                      {formatNumber(row.budgetHours)}
                    </TableCell>
                  </TableRow>
                );
              })}
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell colSpan={4} sx={{ fontWeight: 700 }}>
                  Total
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  {formatNumber(totalBudgetHours)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>
      )}
    </Box>
  );
};

/**
 * Labor Headcount tab — two charts (shop left, field right) and
 * two monthly ±2 month tables below.
 */
const HeadcountTab = ({ projectId }) => {
  const auth = useContext(AuthContext);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [recalculating, setRecalculating] = useState(false);
  const [recalcError, setRecalcError] = useState(null);
  const [drilldown, setDrilldown] = useState(null);
  const [drilldownLoading, setDrilldownLoading] = useState(false);
  const [drilldownError, setDrilldownError] = useState(null);
  const [expandedChart, setExpandedChart] = useState(null);

  // Track viewport height so the full-screen chart can be sized to ~80% of the
  // screen. Syncfusion's chart height only accepts px / % values (not calc()/vh),
  // so we resolve it to a concrete pixel value here.
  const [viewportHeight, setViewportHeight] = useState(() =>
    typeof window !== 'undefined' ? window.innerHeight : 800
  );

  useEffect(() => {
    const handleResize = () => setViewportHeight(window.innerHeight);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await ProductionMetricsAPI.getHeadcountData(
          projectId,
          null,
          auth.token
        );
        if (!cancelled) setData(result);
      } catch (err) {
        if (!cancelled)
          setError(err.message || 'Failed to load headcount data');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [projectId, auth.token]);

  useEffect(() => {
    setDrilldown(null);
    setDrilldownError(null);
    setDrilldownLoading(false);
    setExpandedChart(null);
  }, [projectId]);

  const handleRecalculate = async () => {
    setRecalculating(true);
    setRecalcError(null);
    try {
      await ProductionMetricsAPI.recalculateHeadcount(projectId, auth.token);
      const result = await ProductionMetricsAPI.getHeadcountData(
        projectId,
        null,
        auth.token
      );
      setData(result);
    } catch (err) {
      setRecalcError(
        getProjectDateRangeErrorMessage(err, 'Recalculation failed')
      );
    } finally {
      setRecalculating(false);
    }
  };

  const handlePlanPointDoubleClick = async ({ laborType, weekEnding }) => {
    if (!projectId || !weekEnding || !laborType) return;

    setDrilldown({
      type: laborType,
      weekEnding,
      packages: [],
      totalBudgetHours: 0,
      plannedFTE: 0,
    });
    setDrilldownLoading(true);
    setDrilldownError(null);

    try {
      const result = await ProductionMetricsAPI.getHeadcountActivePackages(
        projectId,
        weekEnding,
        laborType,
        auth.token
      );
      setDrilldown(result);
    } catch (err) {
      setDrilldownError(
        err.message || 'Failed to load active packages for this date'
      );
    } finally {
      setDrilldownLoading(false);
    }
  };

  if (!projectId) {
    return (
      <Typography sx={{ color: 'text.secondary', mt: 4, textAlign: 'center' }}>
        Select a project to view headcount data.
      </Typography>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" sx={{ mt: 4, textAlign: 'center' }}>
        {error}
      </Typography>
    );
  }

  if (!data) return null;

  // ── Transform API data into chart arrays ────────────────────────────
  const shopActual = data.weeklyChart.map((w) => ftePoint(w, 'shop'));
  const shopPlan = data.weeklyChart.map((w) => ({
    x: toCalendarDate(w.weekEnding),
    y: w.plannedShopFTE,
  }));
  const fieldActual = data.weeklyChart.map((w) => ftePoint(w, 'field'));
  const fieldPlan = data.weeklyChart.map((w) => ({
    x: toCalendarDate(w.weekEnding),
    y: w.plannedFieldFTE,
  }));
  const expandedChartTitle =
    expandedChart === 'shop'
      ? 'Shop Headcount FTE'
      : 'Field Headcount FTE';
  const expandedDrilldown =
    expandedChart && drilldown?.type === expandedChart ? drilldown : null;
  // ~80% of the screen when the chart is alone; leave room (~50%) for the
  // active-packages panel when a drilldown is open below it.
  const expandedChartHeight = `${Math.round(
    viewportHeight * (expandedDrilldown ? 0.5 : 0.8)
  )}px`;

  // ── Transform API data into table arrays ────────────────────────────
  const monthLabels = data.months.map((m) => m.label);
  const shopPlanMonth = data.months.map((m) => m.plannedShopFTE);
  const shopActMonth = data.months.map((m) => m.actualShopFTE);
  const shopDelta = data.months.map((m) => m.deltaShop);
  const fieldPlanMonth = data.months.map((m) => m.plannedFieldFTE);
  const fieldActMonth = data.months.map((m) => m.actualFieldFTE);
  const fieldDelta = data.months.map((m) => m.deltaField);

  const hasChartData = data.weeklyChart.length > 0;
  // GC/CO is actuals-only — current month + two prior, no future columns.
  const gccoMonths = data.months.slice(0, 3);
  const gccoLabels = gccoMonths.map((m) => m.label);
  const gccoFte = (bucket, key) =>
    gccoMonths.map((m) =>
      m[bucket] == null ? null : (m[bucket][key] ?? 0)
    );

  return (
    <Box>
      {/* Admin recalculate button + empty state */}
      {recalcError && (
        <Alert
          severity="error"
          onClose={() => setRecalcError(null)}
          sx={{ mb: 1.5 }}
        >
          {recalcError}
        </Alert>
      )}

      {auth.isAdmin && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={
              recalculating ? (
                <CircularProgress size={14} />
              ) : (
                <RefreshIcon sx={{ fontSize: 16 }} />
              )
            }
            disabled={recalculating}
            onClick={handleRecalculate}
            sx={{ textTransform: 'none', fontSize: '12px' }}
          >
            {recalculating ? 'Recalculating…' : 'Recalculate Headcount'}
          </Button>
        </Box>
      )}

      {!hasChartData ? (
        <Typography
          sx={{ color: 'text.secondary', textAlign: 'center', mt: 4 }}
        >
          No headcount schedule data yet.
          {auth.isAdmin &&
            ' Click "Recalculate Headcount" above to generate the planned schedule.'}
        </Typography>
      ) : (
        <>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              mb: 1.5,
              px: 1.5,
              py: 1,
              borderRadius: 1.5,
              bgcolor: 'rgba(55, 138, 221, 0.08)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <InfoOutlinedIcon
                sx={{ fontSize: 16, color: '#378ADD', flexShrink: 0 }}
              />
              <Typography
                sx={{
                  fontSize: '12px',
                  lineHeight: 1.45,
                  color: 'text.primary',
                }}
              >
                Double-click any point on the charts below to see the packages
                contributing to that week
              </Typography>
            </Box>
            {drilldownLoading && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  color: 'text.secondary',
                  flexShrink: 0,
                }}
              >
                <CircularProgress size={13} />
                <Typography sx={{ fontSize: '11px' }}>
                  Loading packages...
                </Typography>
              </Box>
            )}
          </Box>

          {/* Charts — Shop LEFT, Field RIGHT */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)',
              gap: 1.5,
              mb: 1.5,
            }}
          >
            <HCChart
              title="Shop Headcount FTE"
              fillColor="#1D9E75"
              borderColor="#1D9E75"
              actualData={shopActual}
              planData={shopPlan}
              laborType="shop"
              onPlanPointDoubleClick={handlePlanPointDoubleClick}
              onExpand={() => setExpandedChart('shop')}
              expandAriaLabel="Expand shop headcount chart"
            />
            <HCChart
              title="Field Headcount FTE"
              fillColor="#378ADD"
              borderColor="#378ADD"
              actualData={fieldActual}
              planData={fieldPlan}
              laborType="field"
              onPlanPointDoubleClick={handlePlanPointDoubleClick}
              onExpand={() => setExpandedChart('field')}
              expandAriaLabel="Expand field headcount chart"
            />
          </Box>

          <Dialog
            fullScreen
            open={expandedChart !== null}
            onClose={() => setExpandedChart(null)}
          >
            <AppBar sx={{ position: 'relative' }}>
              <Toolbar>
                <Typography variant="h6" sx={{ flex: 1 }}>
                  {expandedChartTitle}
                </Typography>
                <IconButton
                  edge="end"
                  color="inherit"
                  onClick={() => setExpandedChart(null)}
                  aria-label="Close expanded chart"
                >
                  <CloseIcon />
                </IconButton>
              </Toolbar>
            </AppBar>
            <Box
              sx={{
                p: 3,
                height: 'calc(100vh - 64px)',
                overflow: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              {expandedChart === 'shop' && (
                <HCChart
                  title="Shop Headcount FTE"
                  fillColor="#1D9E75"
                  borderColor="#1D9E75"
                  actualData={shopActual}
                  planData={shopPlan}
                  laborType="shop"
                  onPlanPointDoubleClick={handlePlanPointDoubleClick}
                  height={expandedChartHeight}
                />
              )}
              {expandedChart === 'field' && (
                <HCChart
                  title="Field Headcount FTE"
                  fillColor="#378ADD"
                  borderColor="#378ADD"
                  actualData={fieldActual}
                  planData={fieldPlan}
                  laborType="field"
                  onPlanPointDoubleClick={handlePlanPointDoubleClick}
                  height={expandedChartHeight}
                />
              )}
              {expandedChart && (
                <ActivePackagePanel
                  drilldown={expandedDrilldown}
                  loading={drilldownLoading}
                  error={drilldownError}
                  onClose={() => setDrilldown(null)}
                  maxTableHeight="42vh"
                />
              )}
            </Box>
          </Dialog>

          <ActivePackagePanel
            drilldown={drilldown}
            loading={drilldownLoading}
            error={drilldownError}
            onClose={() => setDrilldown(null)}
          />

          {/* Tables — Shop LEFT, Field RIGHT */}
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <HCMonthTable
              title="Shop — Avg Weekly FTE by Month (±2 months)"
              months={monthLabels}
              planRow={shopPlanMonth}
              actualRow={shopActMonth}
              deltaRow={shopDelta}
            />
            <HCMonthTable
              title="Field — Avg Weekly FTE by Month (±2 months)"
              months={monthLabels}
              planRow={fieldPlanMonth}
              actualRow={fieldActMonth}
              deltaRow={fieldDelta}
            />
          </Box>

          <Box
            sx={{
              mt: 1.5,
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <Box sx={{ width: '100%', maxWidth: 480 }}>
              <HCMonthTable
                title="General Conditions & Change Orders — Avg Weekly FTE by Month"
                subtitle="Excluded from the main chart. Same completed-week average as the tables above."
                months={gccoLabels}
                labelMinWidth={160}
                rows={[
                  [
                    'General Conditions',
                    gccoFte('generalConditionsActualFTE', 'total'),
                    false,
                    false,
                  ],
                  [
                    'Change Orders',
                    gccoFte('changeOrdersActualFTE', 'total'),
                    false,
                    false,
                  ],
                ]}
              />
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default HeadcountTab;
